import React from "react";
import { Box, IconButton, Stack, Typography, Drawer, Fade } from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CloseIcon from "@mui/icons-material/Close";
import SwipeIcon from "@mui/icons-material/SwipeLeft";
import { Job } from "../../../store/jobStore";
import { JobCard } from "./JobCard";
import { JobFilterTabs } from "./JobFilterTabs";
import { EmptyJobsState } from "./EmptyJobsState";
import { FilterType, JobCounts } from "../types";

interface Props {
  open: boolean;
  toggleDrawer: () => void;
  activeFilter: FilterType;
  setActiveFilter: React.Dispatch<React.SetStateAction<FilterType>>;
  jobCounts: JobCounts;
  filteredJobs: Job[];
  handleRemoveJob: (job: Job, event: React.MouseEvent) => void;
  handleRetryJob: (job: Job, event: React.MouseEvent) => void;
  handleJobClick: (job: Job) => void;
  newJobIds: Set<string>;
  completedJobIds: Set<string>;
  retryingJob: boolean;
  isMobile: boolean;
  activeJobsCount: number;
}

export const JobMonitorDrawer: React.FC<Props> = ({
  open,
  toggleDrawer,
  activeFilter,
  setActiveFilter,
  jobCounts,
  filteredJobs,
  handleRemoveJob,
  handleRetryJob,
  handleJobClick,
  newJobIds,
  completedJobIds,
  retryingJob,
  isMobile,
  activeJobsCount,
}) => (
  <Drawer
    anchor="right"
    open={open}
    onClose={toggleDrawer}
    sx={{
      zIndex: (theme) => theme.zIndex.drawer,
      "& .MuiDrawer-paper": {
        width: { xs: "100%", sm: 450 },
        boxSizing: "border-box",
        borderLeft: 2,
        borderColor: "divider",
        top: { xs: 56, lg: 64 },
        height: { xs: "calc(100% - 56px)", lg: "calc(100% - 64px)" },
      },
    }}
  >
    <Box sx={{ position: "sticky", top: 0, backgroundColor: "background.paper", zIndex: 1, borderBottom: 1, borderColor: "divider" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2, pt: isMobile ? 10 : 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <AssignmentIcon sx={{ fontSize: 26, color: "primary.main" }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.15rem", lineHeight: 1.2 }}>
              Report Tracker
            </Typography>
            {activeJobsCount > 0 && (
              <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 600, fontSize: "0.8rem" }}>
                {activeJobsCount} {activeJobsCount === 1 ? "report" : "reports"} processing
              </Typography>
            )}
          </Box>
        </Stack>
        <IconButton onClick={toggleDrawer} aria-label="Close" sx={{ "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.08)" } }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <JobFilterTabs activeFilter={activeFilter} jobCounts={jobCounts} onChange={setActiveFilter} />
    </Box>

    <Box sx={{ p: 2, overflowY: "auto", flex: 1 }}>
      {isMobile && filteredJobs.length > 0 && (
        <Fade in timeout={1000}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mb: 2, opacity: 0.6 }}>
            <SwipeIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption">Swipe left to remove</Typography>
          </Stack>
        </Fade>
      )}

      {filteredJobs.length > 0 ? (
        filteredJobs.map((job) => (
          <JobCard
            key={job.id}
            job={job}
            onRemove={handleRemoveJob}
            onRetry={handleRetryJob}
            onClick={handleJobClick}
            isNew={newJobIds.has(job.id)}
            retryingJob={retryingJob}
            showConfetti={completedJobIds.has(job.id)}
          />
        ))
      ) : (
        <EmptyJobsState activeFilter={activeFilter} />
      )}
    </Box>
  </Drawer>
);
